import React, { useRef } from 'react';
import { styled } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { ListItem, List, Grid } from '@mui/material';
import TawkMessengerReact from '@tawk.to/tawk-messenger-react';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import LeakAddIcon from '@mui/icons-material/LeakAdd';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';

const Accordion = styled((props: any) => (
  <MuiAccordion {...props} />
))(() => ({
  'boxShadow': 'none',
  'marginBottom': '12px',
  'borderRadius': '8px',
  'background': 'var(--bg-secondary)',
  'color': 'var(--text-primary)',
  'fontFamily': 'Inter',
  'fontSize': '16px',
  'fontStyle': 'normal',
  'fontWeight': 600,
  'lineHeight': '22px',
  '&:hover': {
    background: '#25262B'
  }
}));

const AccordionSummary = styled((props:any) => (
  <MuiAccordionSummary
    {...props}
  />
))(() => ({
  'backgroundColor': 'rgba(0, 0, 0, .03)',
  'marginBottom': -1,
  'minHeight': 56,
  '&$expanded': {
    minHeight: 56,
  },
  '& .MuiAccordionSummary-expandIcon': {
    color: 'var(--alternative-text-color)'
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    '&$expanded': {
      margin: '12px 0',
    },
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: '10px',
  color: 'var(--card-text)',
  fontFamily: 'Inter',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '21px',
}));

const FAQ: React.FC = () => {
  const [ expanded, setExpanded ] = React.useState<string | false>(false);

  const tawkMessengerRef = useRef<any>();

  const onLoad = () => {
    if (tawkMessengerRef.current) {
      tawkMessengerRef.current.minimize();
    }
  };

  const handleTawkToggle = () => {
    if (tawkMessengerRef.current) {
      tawkMessengerRef.current.toggle();
    }
  };

  const handleChange = (panel: string) => (event: React.ChangeEvent<any>, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const plusIcon = <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <path d='M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z' stroke='var(--card-text)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
  </svg>;

  const minusIcon = <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
    <path d='M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z' stroke='var(--card-text)' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
  </svg>;

  return (
    <section className='home-content-body Home-HeaderDown' style={{ minHeight: 0 }} >
      <Grid container>
        <Grid item xs={12}>
          <div className='module-header'>
            <div className='homepage-header-title-cont'>
              <h1>Help Center</h1>
              <div className='module-header-btns'>
                <a href='https://bitedata.io/contact' target='_blank' rel='noopener noreferrer'>
                  <button className='contact-btn'>
                    <PermContactCalendarIcon />
                  Contact Request
                  </button>
                </a>
              </div>
            </div>
          </div>
        </Grid>
        <div className='HomeDashboard-container'>
          <div className='support-link-grp'>
            <div className='support-module'>
              <a href='https://join.slack.com/t/bitedataupdates/shared_invite/zt-2xjj5u0is-AGY7WosI39BB0ASC2PSkpQ' target='_blank' rel='noopener noreferrer' className='hover-box'>
                <div className='module-icon'>
                  <LeakAddIcon sx={{ fill: '#fff' }}/>
                </div>
                <div className='support-info'>
                  <div className='module-title'>
                    <h2>Connect on Slack</h2>
                  </div>
                  <div className='module-desc'>
                    <Typography
                      variant='subtitle2'
                    >
                  Communicate with our team about any questions you have.
                    </Typography>
                  </div>
                </div>
              </a>
            </div>


            <div className='support-module'>
              <a href='mailto:info@bitedata.io' className='hover-box'>
                <div className='module-icon'>
                  <ContactSupportIcon sx={{ fill: '#fff' }}/>
                </div>
                <div className='support-info'>
                  <div className='module-title'>
                    <h2>Contact Us</h2>
                  </div>
                  <div className='module-desc'>
                    <Typography
                      variant='subtitle2'
                    >
                  Have a specific question? Reach out to us at info@bitedata.io.
                    </Typography>
                  </div>
                </div>
              </a>
            </div>

            <div className='support-module'>
              <a className='hover-box' onClick={handleTawkToggle}>
                <div className='module-icon'>
                  <QuestionAnswerIcon sx={{ fill: '#fff' }} />
                </div>
                <div className='support-info'>
                  <div className='module-title'>
                    <h2>Chat Support</h2>
                  </div>
                  <div className='module-desc'>
                    <Typography
                      variant='subtitle2'
                    >
                  Have an urgent question? Click on the chat bubble to get started.
                    </Typography>
                  </div>
                </div>
              </a>
            </div>
          </div>
          <div className='homepage-header-title-cont'>
            <h1>FAQs</h1>
          </div>
          <div className='faq-container'>
            <Accordion square expanded={expanded === 'panel1'} onChange={handleChange('panel1')} style={{ background: 'transparent', width: '80%', color: 'var(--text-primary)' }}>
              <AccordionSummary aria-controls='panel1d-content' id='panel1d-header' expandIcon={expanded === 'panel1' ? minusIcon : plusIcon} >
                <Typography>What is BITE?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
            The Business Intelligence for Trade Enforcement (BITE) platform is a cost-effective and
            easy-to-use platform that allows companies and organizations to monitor, identify and mitigate
            risk in their supply chain, and remain on the right side of trade compliance.
                  <br></br>
                  <br></br>
            The platform includes two core modules:
                  <br></br>
                  <List>
                    <ListItem>
                    A Web application that helps compliance and enforcement officers access the latest
                    watchlists, sanctions lists, commodity regimes, cyber vulnerabilities, and more to help
                    identify risk in real-time.
                    </ListItem>
                    <ListItem>
                  Artificial Intelligence (AI) platform that includes millions of worldwide trade records.
                  BITE engineers and subject matter experts use the data to investigate specific areas for
                  customers and create customized reports, and identify entities that are partaking in risky
                  trade practices. These entities are added to the High Risk Entities List and available via the Web app.
                    </ListItem>
                  </List>
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion square expanded={expanded === 'panel2'} onChange={handleChange('panel2')} style={{ background: 'transparent', width: '80%', color: 'var(--text-primary)' }}>
              <AccordionSummary aria-controls='panel2d-content' id='panel2d-header' expandIcon={expanded === 'panel2' ? minusIcon : plusIcon}>
                <Typography>Who uses BITE?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
            BITE is used by private and government personnel across multiple compliance and enforcement domains.
            Check out our cases here for info on the different types of activities and groups we support.
                  <br></br>
                  <br></br>
            The tool is used by multiple agencies in the US and EU.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion square expanded={expanded === 'panel3'} onChange={handleChange('panel3')} style={{ background: 'transparent', width: '80%', color: 'var(--text-primary)' }}>
              <AccordionSummary aria-controls='panel3d-content' id='panel3d-header' expandIcon={expanded === 'panel3' ? minusIcon : plusIcon}>
                <Typography>Where can I learn more about BITE?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
              Take a look at this overview video of the platform&nbsp;
                  <a href='https://www.youtube.com/watch?v=qGYDF7mlh-Y' target='_blank' rel='noreferrer'>here</a>
                  <br></br>
                  <br></br>
              You can also take a look at the Products page as well as our Blog page to learn more about what we do.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion square expanded={expanded === 'panel4'} onChange={handleChange('panel4')} style={{ background: 'transparent', width: '80%', color: 'var(--text-primary)' }}>
              <AccordionSummary aria-controls='panel3d-content' id='panel3d-header' expandIcon={expanded === 'panel4' ? minusIcon : plusIcon}>
                <Typography>How much does BITE Cost?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
            BITE is priced very competitively, and all our pricing is transparent, listed&nbsp;
                  <a href='https://www.bitedata.io/pricing' target='_blank' rel='noreferrer'>here</a>
                  <br></br>
                  <br></br>
            Contact us for more information regarding bundling and discounts -&nbsp;
                  <a href='mailto:info@bitedata.io'>info@bitedata.io</a>
            .
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion square expanded={expanded === 'panel5'} onChange={handleChange('panel5')} style={{ background: 'transparent', width: '80%', color: 'var(--text-primary)' }}>
              <AccordionSummary aria-controls='panel3d-content' id='panel3d-header' expandIcon={expanded === 'panel5' ? minusIcon : plusIcon}>
                <Typography>Can I access BITE via API?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
            Yes - all data in our web app is available via Application Programming Interface (API).
            For API access and documentation, reach out to us at{' '}
            <a href='mailto:info@bitedata.io'>info@bitedata.io</a>.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion square expanded={expanded === 'panel6'} onChange={handleChange('panel6')} style={{ background: 'transparent', width: '80%', color: 'var(--text-primary)' }}>
              <AccordionSummary aria-controls='panel3d-content' id='panel3d-header' expandIcon={expanded === 'panel6' ? minusIcon : plusIcon}>
                <Typography>Where can I get more information about BITE?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
            Have more questions - contact us to set up a call&nbsp;
                  <a href='https://www.bitedata.io/contact' target='_blank' rel='noreferrer'>here</a>.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
      </Grid>
      <TawkMessengerReact
        propertyId='663c243d07f59932ab3d73e6'
        widgetId='1htder4kb'
        onLoad={onLoad}
        ref={tawkMessengerRef}
      />
    </section>
  );
};

export default FAQ;
